import { useQuery } from '@tanstack/react-query';
import { checkSlugAvailable, RESERVED_SLUGS } from '@dukanti/shared';
import { supabase } from '@/lib/supabase';

/** Slug validation regex: 3-60 lowercase letters, numbers, hyphens only.
 *  Must not start or end with a hyphen. */
const SLUG_REGEX = /^[a-z0-9]+(-[a-z0-9]+)*$/;

export interface SlugCheckResult {
  /** null = not checked yet, true = available, false = taken */
  isAvailable: boolean | null;
  /** true while the query is in flight */
  isChecking: boolean;
}

/**
 * Debounced slug uniqueness check. The query only fires when:
 * - slug passes local validation (format, length, not reserved)
 * - 300ms have elapsed since the last slug change (via React Query's staleTime)
 *
 * The debounce is implemented by only enabling the query when the slug
 * is locally valid, combined with React Query's refetchOnMount: false.
 * The actual debounce delay is handled by the caller (the form component
 * debounces the slug value before passing it to this hook).
 */
export function useCheckSlug(slug: string): SlugCheckResult {
  const isLocallyValid =
    slug.length >= 3 && slug.length <= 60 && SLUG_REGEX.test(slug) && !RESERVED_SLUGS.has(slug);

  const query = useQuery({
    queryKey: ['slug-check', slug],
    enabled: isLocallyValid,
    staleTime: 10_000, // Cache result for 10s
    queryFn: () => checkSlugAvailable(supabase, slug),
  });

  if (!isLocallyValid) {
    return { isAvailable: null, isChecking: false };
  }

  return {
    isAvailable: query.data ?? null,
    isChecking: query.isLoading || query.isFetching,
  };
}
